
import React, { PureComponent } from 'react';
import { get, noop } from 'lodash';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import * as primitives from '../../primitives';
import { Breakpoints, getWindowWidth } from '../../../grid';
import {
    insurancePickerPropsShape,
    LOCALIZED_STRINGS,
    locationPickerPropsShape,
    ppsUniversalSearchPropsShape,
    SearchApiEndpoint,
    SearchApiV2Endpoint,
} from '../../constants';
import {
    getCookiedInsuranceFromUserSpecialtyType,
    getUserSpecialtyTypeFromSpecialtyId,
    isMobileBreakpoint,
} from '../../../utils/index';
import { UniversalSearch } from '../../../universalsearch';
import LocationPicker from '../../../LocationPicker';

const dataProdGuid = '384e1e65';

class SearchBarView extends PureComponent {
    static propTypes = {
        awsApiHost: PropTypes.string,
        fetchCarriersIfNeeded: PropTypes.func,
        fetchPlansIfNeeded: PropTypes.func,
        insurancePickerProps: insurancePickerPropsShape,
        locationPickerProps: locationPickerPropsShape,
        ppsUniversalSearchProps: ppsUniversalSearchPropsShape,
        selectCarrier: PropTypes.func,
        selectPlan: PropTypes.func,
        setCarrier: PropTypes.func,
        setInsuranceType: PropTypes.func,
        setPlan: PropTypes.func,
        showCollapseToggle: PropTypes.bool,
        theme: PropTypes.object,
        trackPpsChange: PropTypes.func,
        trackPpsFocus: PropTypes.func,
        trackPpsSelect: PropTypes.func,
        trackSubmitSearch: PropTypes.func,
    };

    static defaultProps = {
        awsApiHost: process.env.AWS_API_HOST,
        trackPpsChange: noop,
        trackPpsFocus: noop,
        trackPpsSelect: noop,
        trackSubmitSearch: noop,
    };

    state = {
        isExpanded: false,
        pendingSearchParams: {},
    }

    render() {
        const {
            className,
        } = this.props;

        const {
            isExpanded,
        } = this.state;

        return (
            <primitives.SearchBarContainer>
                <primitives.SearchForm
                    action="#"
                    className={className}
                    data-test="search-bar-form"
                    isExpanded={isExpanded}
                    onSubmit={this.handleSubmit}
                >
                    {this.renderPpsSearchField()}
                    {this.renderStyledLocationWrapper()}
                    {this.renderInsuranceFieldWrapper()}

                    <primitives.Expandable expanded={isExpanded}>
                        {this.renderSearchButton()}
                        {this.maybeRenderCollapseToggle()}
                    </primitives.Expandable>
                </primitives.SearchForm>
            </primitives.SearchBarContainer>
        );
    }

    maybeRenderCollapseToggle = () => {
        const {
            showCollapseToggle,
            theme,
        } = this.props;

        if (showCollapseToggle) {
            return (
                <primitives.TextRight>
                    <primitives.CollapseToggle onClick={this.toggleView}>
                        <primitives.StyledArrow
                            color={theme.search.bar.mobileCollapseArrow.bgColor}
                            direction="up"
                        />
                        <primitives.StyledClose>
                            <FormattedMessage {...LOCALIZED_STRINGS.Close} />
                        </primitives.StyledClose>
                    </primitives.CollapseToggle>
                </primitives.TextRight>
            );
        }
    }

    renderSearchButton = () => (
        <primitives.SearchButton
            data-prod-guids={`search-bar-search-button:[${dataProdGuid}]`}
            data-test="search-bar-search-button"
            type="submit"
        >
            <primitives.SearchButtonText>
                <FormattedMessage {...LOCALIZED_STRINGS.FindDoctors} />
            </primitives.SearchButtonText>
            <primitives.StyledMagnifyingGlass color="currentColor" />
        </primitives.SearchButton>);

    renderStyledLocationWrapper = () => {
        const {
            locationPickerProps,
        } = this.props;

        const {
            isExpanded,
        } = this.state;

        return (
            <primitives.LocationSearchFieldWrapper
                key="location-field"
                expanded={isExpanded}
            >
                <primitives.SearchFieldLabel>
                    <FormattedMessage {...LOCALIZED_STRINGS.Location} />
                </primitives.SearchFieldLabel>
                <LocationPicker
                    {...locationPickerProps}
                    onChange={this.handleLocationChange}
                />
            </primitives.LocationSearchFieldWrapper>
        );
    }

    renderInsuranceFieldWrapper = () => {
        const {
            insurancePickerProps,
        } = this.props;

        const {
            isExpanded,
        } = this.state;

        return (
            <primitives.InsuranceSearchFieldWrapper
                key="insurance-picker-field"
                expanded={isExpanded}
            >
                <primitives.SearchFieldLabel>
                    <FormattedMessage {...LOCALIZED_STRINGS.Insurance} />
                </primitives.SearchFieldLabel>
                <primitives.StyledInsurancePicker
                    {...insurancePickerProps}
                    onChange={this.handleInsuranceChange}
                />
            </primitives.InsuranceSearchFieldWrapper>
        );
    }

    renderPpsSearchField = () => {
        const {
            ppsUniversalSearchProps,
            awsApiHost,
            theme,
        } = this.props;

        const {
            isExpanded,
        } = this.state;

        ppsUniversalSearchProps.directoryId = String(theme.directoryId);
        ppsUniversalSearchProps.isMobile = isMobileBreakpoint();
        ppsUniversalSearchProps.searchApiUrl = awsApiHost + SearchApiEndpoint;
        ppsUniversalSearchProps.searchApiV2Url = awsApiHost + SearchApiV2Endpoint;
        ppsUniversalSearchProps.onChangeValue = this.handlePpsChange;
        ppsUniversalSearchProps.onSelectOption = this.handlePpsSelectOption;
        ppsUniversalSearchProps.onTextBoxFocus = this.handlePpsFocus;

        return (
            <primitives.PpsSearchFieldWrapper key="pps-field">
                <primitives.Expandable expanded={isExpanded}>
                    <primitives.SearchFieldLabel>
                        <FormattedMessage {...LOCALIZED_STRINGS.Search} />
                    </primitives.SearchFieldLabel>
                </primitives.Expandable>
                <UniversalSearch
                    {...ppsUniversalSearchProps}
                />
                {/*
                    The first tap on the mobile PPS field should expand the fields, not set
                    focus to the textbox. Solution: Create a transparent div that covers the PPS bar
                    and disappears when expanded.
                */}
                {!isExpanded ? <primitives.Toggle onClick={this.toggleView} /> : null}
            </primitives.PpsSearchFieldWrapper>);
    }

    handleInsuranceChange = insurance => {
        const {
            pendingSearchParams,
        } = this.state;

        const carrierId = get(insurance, 'carrier.id');
        const planId = get(insurance, 'plan.id');

        if (carrierId) {
            this.updatePendingSearchParams({
                carrierId,
                planId: planId || pendingSearchParams.planId,
            });
        }
    }

    handleLocationChange = address => {
        this.updatePendingSearchParams({ address });
    }

    handlePpsSelectOption = option => {
        const {
            gaCategory,
            trackPpsSelect,
            specialtyId,
            setInsuranceType,
            clearCarrierAndPlan,
            selectCarrier,
            setCarrier,
            selectPlan,
            setPlan,
            fetchCarriersIfNeeded,
            fetchPlansIfNeeded,
        } = this.props;

        const {
            pendingSearchParams,
        } = this.state;

        const selectionType = option.name === option.procedureName ? 'procedure' : 'specialty';

        trackPpsSelect({
            selection: option.name,
            selectionType,
            gaCategory,
        });

        let newPendingParams = {
            specialtyId: option.specialtyId,
            procedureId: option.procedureId,
        };

        const newUserSpecialtyInsuranceType = getUserSpecialtyTypeFromSpecialtyId(option.specialtyId);
        // Current specialty type is firstly from pending params, secondly search param state
        const curUserSpecialtyInsuranceType =
            getUserSpecialtyTypeFromSpecialtyId(pendingSearchParams.specialtyId || specialtyId);

        if (newUserSpecialtyInsuranceType !== curUserSpecialtyInsuranceType) {
            // User is switching to a new specialty insurance
            // This insuranceType prop only updates the insurance type for search reasons
            setInsuranceType(option.specialtyInsuranceTypeId);
            // Check cookies to see if user has existing insurance of the new specialty
            const cookiedInsurance = getCookiedInsuranceFromUserSpecialtyType(newUserSpecialtyInsuranceType);

            if (!cookiedInsurance) {
                clearCarrierAndPlan();
                newPendingParams.carrierId = '-1';
                newPendingParams.planId = '-1';
            } else {
                // Cannot send data via a prop update because there is no componentDidUpdate hook in
                // the InsurancePickerWrapper that handles changes in carrierId/planId.
                // However to avoid more side effects and bugs with other use cases of the insurance picker,
                // instead we'll scope necessary InsurancePicker actions here within the SearchBar
                selectCarrier(cookiedInsurance.carrierId);
                setCarrier({
                    id: cookiedInsurance.carrierId,
                    name: 'Loading...', // A loading string picker before carrierId name is fetched from redux or APIs
                }, false);
                selectPlan(cookiedInsurance.planId);
                setPlan({ id: cookiedInsurance.planId }, false);
                fetchCarriersIfNeeded();
                fetchPlansIfNeeded(cookiedInsurance.carrierId);
                newPendingParams = {
                    ...newPendingParams,
                    ...cookiedInsurance,
                };
            }
        }

        this.updatePendingSearchParams(newPendingParams);
    }

    handlePpsChange = value => {
        const {
            gaCategory,
            trackPpsChange,
        } = this.props;

        trackPpsChange({
            query: value,
            gaCategory,
        });
    }

    handlePpsFocus = () => {
        const {
            gaCategory,
            trackPpsFocus,
        } = this.props;

        trackPpsFocus(gaCategory);
    }

    toggleView = () => {
        const {
            isExpanded,
        } = this.state;

        this.setState({
            isExpanded: !isExpanded,
        });
    }

    submitPendingSearchParams() {
        const {
            redirectToSearch,
        } = this.props;

        const {
            pendingSearchParams,
        } = this.state;

        redirectToSearch(pendingSearchParams);
    }

    handleSubmit = async event => {
        const {
            trackSubmitSearch,
            address,
            fetchCurrentAddress,
        } = this.props;

        const {
            pendingSearchParams,
        } = this.state;

        event.preventDefault();

        trackSubmitSearch(event);

        const hasAddressParam = address || pendingSearchParams.address;

        if (!hasAddressParam && getWindowWidth() < Breakpoints.small) {
            // Try to use geolocation API to get user's location.
            // Note: Only try to do this on mobile, because the geolocation API is slow on desktop
            this.updatePendingSearchParams({ address: await fetchCurrentAddress() });
        }

        this.submitPendingSearchParams();
    }

    updatePendingSearchParams(params) {
        const {
            pendingSearchParams,
        } = this.state;

        this.setState({
            pendingSearchParams: {
                ...pendingSearchParams,
                ...params,
            },
        });
    }
}

export default SearchBarView;
